import { isDev, isInternal, isLocal } from "../utils/env";
import { log } from "../utils/logger";
import async from "../components/Async";

// Immediate Pages - Any other page could be loaded asynchronously to improve performance
import SimpleLayout from "../layouts/Simple";
import Error401 from "../pages/Error/401";
import Error404 from "../pages/Error/404";
import Register from "../pages/Auth/Register";
import SignIn from "../pages/Auth/SignIn";
import SSOSuccess from "../pages/Auth/SSO.Success";
import Home from "../pages/Home";
import CentralizedServicesToken from "../pages/Auth/CentralizedServices.Token";
import FileUpload from "../pages/FileUpload/fileUpload";

declare global {
  interface RouteTypes {
    // Name to be displayed (if applicable)
    name: string;
    // Path to be used for this route
    path: string;
    // Where to redirect if path is hit
    redirectTo?: string;
    // The component to be displayed
    component?: any;
    // The layout to use (overrides default)
    layout?: any;
    // Where item should route if different from "path"
    link?: string;
    // Whether item should be prepend a header
    header?: string;
    // Font Awesome Icon
    icon?: any;
    // Support hidden components
    isPrivate?: boolean;
    // Required permission scope to access the resource
    restrictedTo?: string[];
    // If the component has nested routes
    children?: RouteTypes[];
  }
}

// ============================================================================
// Route Configuration ========================================================
// ============================================================================

// Error Pages
const ErrorRoutes: RouteTypes = {
  name: "Errors",
  path: "/error",
  children: [
    {
      path: "/401",
      name: "401 Unauthorized",
      component: Error401,
    },
    {
      path: "/404",
      name: "404 Not Found",
      component: Error404,
    },
  ],
};
export const error: RouteTypes[] = [ErrorRoutes];

// Authentication Routes
const authRoutes: RouteTypes = {
  name: "Authentication",
  path: "/auth",
  children: [
    {
      path: "/auth/register",
      name: "Register",
      component: Register,
    },
    {
      path: "/auth/sign-in",
      name: "Sign In",
      component: SignIn,
    },
    {
      path: "/auth/sso-success",
      name: "SSO Success",
      component: SSOSuccess,
    },
    {
      path: "/auth/cs-token",
      name: "Token Log In",
      component: CentralizedServicesToken,
      layout: SimpleLayout,
    },
    {
      path: "/auth/reset-password",
      name: "Reset Password",
      component: async(() => import("../pages/UserManagement/ResetPassword")),
    },
    {
      path: "/auth/forgot-password",
      name: "Forgot Password",
      component: async(() => import("../pages/UserManagement/ForgotPassword")),
    },
  ],
};
export const auth: RouteTypes[] = [authRoutes];

// Preference Management Routes
const prefRoutes: RouteTypes = {
  name: "Preference Management",
  path: "/preference",
  isPrivate: true,
  children: [
    {
      path: "/preference/dashboard",
      name: "PrefSearch",
      component: async(() => import("../pages/PreferenceManagement/Search")),
      restrictedTo: ["account.read", "account.readWrite"],
    },
    {
      path: "/preference/add",
      name: "PrefAdd",
      component: async(() => import("../pages/PreferenceManagement/Card.AddAccount")),
      restrictedTo: ["account.readWrite"],
    },
    {
      path: "/preference/keyword-simulator",
      name: "KeywordSimulator",
      component: async(() => import("../pages/KeywordSimulator")),
      restrictedTo: ["account.read", "account.readWrite"],
    },
  ],
};
export const pref: RouteTypes[] = [prefRoutes];

// Account Management Routes
const accountRoutes: RouteTypes = {
  name: "Account Management",
  path: "/account",
  isPrivate: true,
  children: [
    {
      path: "/account/edit",
      name: "AccountEdit",
      component: async(() => import("../pages/AccountManagement/Edit")),
      restrictedTo: ["account.readWrite"],
    },
    {
      path: "/account/communications",
      name: "AccountCommunications",
      component: async(() => import("../pages/AccountManagement/Communications")),
      restrictedTo: ["account.read", "account.readWrite"],
    },
    {
      path: "/account/audit",
      name: "AccountAudit",
      component: async(() => import("../pages/AccountManagement/Audit")),
      restrictedTo: ["account.read", "account.readWrite"],
    },
    // {
    //   path: "/account/notes",
    //   name: "AccountNotes",
    //   component: async(() => import("../pages/AccountManagement/Notes")),
    //   restrictedTo: ["account.read", "account.readWrite"],
    // },
  ],
};
export const account: RouteTypes[] = [accountRoutes];

// Reporting Routes
const reportingRoutes: RouteTypes = {
  name: "Reporting",
  path: "/reporting",
  isPrivate: true,
  children: [
    {
      path: "/reporting/dashboard",
      name: "ReportingDash",
      component: async(() => import("../pages/Reporting")),
      restrictedTo: ["reporting.read", "reporting.readWrite"],
    },
    {
      path: "/reporting/change",
      name: "ReportingChange",
      component: async(() => import("../pages/Reporting/ChangeView")),
      restrictedTo: ["reporting.read", "reporting.readWrite"],
    },
    {
      path: "/reporting/view-report",
      name: "ViewReport",
      component: async(() => import("../pages/Reporting/ViewReport")),
      restrictedTo: ["reporting.read", "reporting.readWrite"],
    },
  ],
};
export const reporting: RouteTypes[] = [reportingRoutes];

// Switchboard Route
const switchboardRoute: RouteTypes = {
  path: "/switchboard/dashboard",
  name: "Switchboard",
  isPrivate: true,
  restrictedTo: ["switchboard.read", "switchboard.readWrite"],
  component: async(() => import("../pages/Switchboard")),
};
export const switchboard: RouteTypes[] = [switchboardRoute];

// UserManagement Routes
const userManagementRoutes: RouteTypes = {
  name: "UserManagement",
  path: "/user-management",
  isPrivate: true,
  children: [
    {
      path: "/user-management/add-new-user",
      name: "AddNewUser",
      component: async(() => import("../pages/UserManagement/AddNewUser")),
      restrictedTo: ["userManagement.readWrite"],
    },
    {
      path: "/user-management/current-users",
      name: "CurrentUsers",
      component: async(() => import("../pages/UserManagement/CurrentUsers")),
      restrictedTo: ["userManagement.read", "userManagement.readWrite"],
    },
    {
      path: "/user-management/view-user",
      name: "ViewUser",
      component: async(() => import("../pages/UserManagement/ViewUser")),
      restrictedTo: ["userManagement.read"],
    },
    {
      path: "/user-management/account-settings",
      name: "AccountSettings",
      component: async(() => import("../pages/UserManagement/AccountSettings")),
    },
  ],
};
export const userManagement: RouteTypes[] = [userManagementRoutes];

// Home Route
const homeRoute: RouteTypes = {
  path: "/home/dashboard",
  name: "Home",
  isPrivate: true,
  component: Home,
};
export const home: RouteTypes[] = [homeRoute];

// File Upload Route
const fileUploadRoute: RouteTypes = {
  path: "/file",
  name: "File Upload",
  isPrivate: true,
  children: [
    {
      path: "/file/lse",
      name: "LSE",
      component: FileUpload,
      restrictedTo: ["fileUpload.read", "fileUpload.readWrite"],
    },
    {
      path: "/file/lseprestorm",
      name: "LSEPRESTORM",
      component: FileUpload,
      restrictedTo: ["fileUpload.read", "fileUpload.readWrite"],
    },
  ],
};
export const fileUpload: RouteTypes[] = [fileUploadRoute];

// Routes that should be displayed in side-nav (in order)
const NavRoutes = [authRoutes];

if (isLocal) {
  log("---- Adding LOCAL only test routes ----");
  NavRoutes.push(/* Local Only Routes go here */);
}
if (isDev) {
  log("---- Adding DEV only test routes ----");
  NavRoutes.push(/* Dev Only Routes go here */);
}
if (isInternal) {
  log("---- Adding INTERNAL only test routes ----");
  NavRoutes.push(/* Internal Only Routes go here */);
}

export default NavRoutes;
